import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import {
  listMyServerProjects,
  starProject,
  unstarProject,
  type ServerProject,
} from './portfolioApi';
import './PortfolioPage.css';

/**
 * Kid portfolio — `/portfolio`.
 *
 * "Things I made and chose to keep." A maker's bench, not a
 * leaderboard.
 *
 * Two sections on one page:
 *   * Starred — the kid's chosen "best work."
 *   * Other synced — the rest of the kid's server-synced projects,
 *     each with a "Star" toggle so adding to the portfolio is one
 *     click. Without this, there'd be no surface anywhere in the
 *     studio to *add* a project to the portfolio.
 *
 * Local-only projects (not yet synced) don't appear here. The kid
 * needs to click "Sync to my account" in the studio header first —
 * that's an explicit, intentional moment in the local-first flow.
 */
export function PortfolioPage(): JSX.Element {
  const { data: user, isPending: userPending } = useCurrentUser();
  const all = useQuery({
    queryKey: ['portfolio', 'all-server-projects'],
    queryFn: listMyServerProjects,
    enabled: Boolean(user),
  });
  const qc = useQueryClient();
  const star = useMutation({
    mutationFn: (id: string) => starProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolio', 'all-server-projects'] });
    },
  });
  const unstar = useMutation({
    mutationFn: (id: string) => unstarProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolio', 'all-server-projects'] });
    },
  });

  if (userPending) {
    return (
      <div className="bl-page">
        <AppHeader subBrand="Portfolio" privacy="on-device" />
        <div className="bl-portfolio" data-testid="portfolio-loading" />
        <AppFooter />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="bl-page">
        <AppHeader subBrand="Portfolio" privacy="on-device" />
        <div className="bl-portfolio">
          <p className="bl-portfolio__notice">Sign in to start a portfolio of your work.</p>
        </div>
        <AppFooter />
      </div>
    );
  }

  const projects = all.data ?? [];
  const starred = projects
    .filter((p) => p.starred_at !== null)
    .sort((a, b) =>
      // Newest-starred first.
      (b.starred_at ?? '').localeCompare(a.starred_at ?? ''),
    );
  const unstarred = projects
    .filter((p) => p.starred_at === null)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));

  return (
    <div className="bl-page">
      <AppHeader subBrand="Portfolio" privacy="on-device" />
      <div className="bl-portfolio">
        <header className="bl-portfolio__header">
          <h1 className="bl-portfolio__title">Things I made</h1>
          <p className="bl-portfolio__subtitle">
            Star your best work. Only you see this — it&apos;s your maker&apos;s bench.
          </p>
        </header>

        {all.isPending ? (
          <p className="bl-portfolio__notice">Loading…</p>
        ) : all.isError ? (
          <p className="bl-portfolio__notice">Couldn&apos;t load your portfolio.</p>
        ) : projects.length === 0 ? (
          <div className="bl-portfolio__empty">
            <p>Nothing synced yet.</p>
            <p className="bl-portfolio__empty-hint">
              Open a project in the studio and click &quot;Sync to my account&quot; — then come back
              to star it.
            </p>
            <Link to="/studio" className="bl-portfolio__empty-link">
              Open the studio →
            </Link>
          </div>
        ) : (
          <>
            <h2 className="bl-portfolio__section-h">Starred</h2>
            {starred.length === 0 ? (
              <p className="bl-portfolio__notice">
                Nothing starred yet. Use the ★ button below to add work you&apos;re proud of.
              </p>
            ) : (
              <ul className="bl-portfolio__list">
                {starred.map((p) => (
                  <Row
                    key={p.id}
                    project={p}
                    starred={true}
                    onStar={() => star.mutate(p.id)}
                    onUnstar={() => unstar.mutate(p.id)}
                    busy={star.isPending || unstar.isPending}
                  />
                ))}
              </ul>
            )}

            {unstarred.length > 0 ? (
              <>
                <h2 className="bl-portfolio__section-h bl-portfolio__section-h--muted">
                  Other synced projects
                </h2>
                <ul className="bl-portfolio__list">
                  {unstarred.map((p) => (
                    <Row
                      key={p.id}
                      project={p}
                      starred={false}
                      onStar={() => star.mutate(p.id)}
                      onUnstar={() => unstar.mutate(p.id)}
                      busy={star.isPending || unstar.isPending}
                    />
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </div>
      <AppFooter />
    </div>
  );
}

function Row(props: {
  project: ServerProject;
  starred: boolean;
  onStar: () => void;
  onUnstar: () => void;
  busy: boolean;
}): JSX.Element {
  const { project: p, starred, onStar, onUnstar, busy } = props;
  return (
    <li className="bl-portfolio__row">
      <div>
        <Link to={`/studio/p/${p.id}`} className="bl-portfolio__row-name">
          {p.name}
        </Link>
        <div className="bl-portfolio__row-meta">
          {p.kind} · {humanizeRelative(p.starred_at ?? p.updated_at)}
        </div>
      </div>
      {starred ? (
        <button
          type="button"
          className="bl-portfolio__unstar"
          onClick={onUnstar}
          disabled={busy}
          aria-label={`Unstar ${p.name}`}
          title="Remove from portfolio"
        >
          ★ Starred
        </button>
      ) : (
        <button
          type="button"
          className="bl-portfolio__star"
          onClick={onStar}
          disabled={busy}
          aria-label={`Star ${p.name}`}
          title="Add to portfolio"
        >
          ☆ Star
        </button>
      )}
    </li>
  );
}

function humanizeRelative(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86_400)}d ago`;
}
